import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  AddToAgendaRequest,
  DelegateRequestDto,
  TaskDto,
  useAddTaskToAgenda,
  useCreateTask,
  useDeleteTask,
  useGenerateDelegateMessage,
  useListTasks,
  usePatchTask
} from '@/api/generated';

const useTasks = (
  status?: string,
  sourceId?: string,
  fetchAll?: boolean,
  from?: number,
  to?: number
) => {
  const queryClient = useQueryClient();
  const {
    data: taskListResponse,
    refetch: refetchTasks,
    isLoading: taskListLoading
  } = useListTasks(
    { status, sourceId, from, to },
    {
      query: {
        queryKey: ['listTasks', status, sourceId, from, to],
        enabled: !!status || !!sourceId || fetchAll || !!from || !!to
      }
    }
  );

  const taskList = useMemo(() => taskListResponse?.items || [], [taskListResponse]);

  const { mutateAsync: mutateCreateTask } = useCreateTask({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['listTasks'] });
      }
    }
  });
  const { mutateAsync: mutatePatchTask } = usePatchTask({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['listTasks'] });
      }
    }
  });
  const { mutateAsync: mutateDeleteTask } = useDeleteTask({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['listTasks'] });
      }
    }
  });
  const { mutateAsync: mutateuseAddTaskToAgenda } = useAddTaskToAgenda();
  const {
    mutateAsync: mutateGenerateDelegateMessage,
    isPending: isGenerateDelegateMessagePending
  } = useGenerateDelegateMessage();

  const createTask = useCallback(
    async (task: TaskDto) => {
      const response = await mutateCreateTask({ data: task });
      return response?.items?.[0];
    },
    [mutateCreateTask]
  );

  const generateDelegateMessage = useCallback(
    async (request: DelegateRequestDto, delegateType: string) => {
      const response = await mutateGenerateDelegateMessage({
        data: request,
        params: { delegateType: delegateType }
      });
      return response?.items?.[0];
    },
    [mutateGenerateDelegateMessage]
  );

  const patchTask = useCallback(
    async (id: string, task: TaskDto) => {
      await mutatePatchTask({ id, data: task });
    },
    [mutatePatchTask]
  );

  const deleteTask = useCallback(
    async (id: string) => {
      await mutateDeleteTask({ id });
    },
    [mutateDeleteTask]
  );

  const addTaskToAgenda = useCallback(
    async (id: string, request: AddToAgendaRequest) => {
      await mutateuseAddTaskToAgenda({ id, data: request });
    },
    [mutateuseAddTaskToAgenda]
  );

  const getNewTasksCount = useCallback(() => {
    let counter = 0;
    const twentyFourHoursAgo = new Date().getTime() - 24 * 60 * 60 * 1000;
    taskList?.forEach(task => {
      if (task.createdAt) {
        const taskDate = new Date(task.createdAt).getTime();
        if (taskDate > twentyFourHoursAgo) {
          counter++;
        }
      }
    });
    return counter;
  }, [taskList]);

  return {
    createTask,
    patchTask,
    deleteTask,
    taskList,
    taskListLoading,
    refetchTasks,
    addTaskToAgenda,
    generateDelegateMessage,
    isGenerateDelegateMessagePending,
    getNewTasksCount
  };
};

export default useTasks;
